import { useRef, useState, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize2, RotateCcw } from "lucide-react";

export default function BeforeAfter({ beforeSrc, afterSrc }) {
  const [pos, setPos] = useState(50);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const ref = useRef(null);
  const sliderDragging = useRef(false);

  useEffect(() => {
    const onUp = () => { dragging.current = false; sliderDragging.current = false; };
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mouseup", onUp); window.removeEventListener("touchend", onUp); };
  }, []);

  const move = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  };

  const fullscreen = () => {
    if (!ref.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else ref.current.requestFullscreen?.();
  };
  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); setPos(50); };

  return (
    <div className="space-y-3">
      <div
        ref={ref}
        className="relative overflow-hidden rounded-md border border-white/10 bg-black select-none"
        style={{ aspectRatio: "16/10" }}
        data-testid="before-after-viewer"
        onMouseMove={(e) => { if (sliderDragging.current) move(e.clientX); if (dragging.current) setPan((p) => ({ x: p.x + e.movementX, y: p.y + e.movementY })); }}
        onTouchMove={(e) => { if (sliderDragging.current && e.touches[0]) move(e.touches[0].clientX); }}
      >
        <div
          className="absolute inset-0"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center" }}
          onMouseDown={() => { dragging.current = true; }}
        >
          <img src={beforeSrc} alt="Avant" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
          <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
            <img src={afterSrc} alt="Après" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
          </div>
        </div>
        <div className="absolute top-0 bottom-0 w-px bg-white/70" style={{ left: `${pos}%` }} />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white/90 text-black grid place-items-center cursor-ew-resize shadow-lg"
          style={{ left: `${pos}%` }}
          onMouseDown={() => { sliderDragging.current = true; }}
          onTouchStart={() => { sliderDragging.current = true; }}
          data-testid="before-after-handle"
        >
          <span className="text-[10px] font-mono tracking-tight">⇋</span>
        </div>
        <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/50 backdrop-blur-md text-[10px] tracking-[0.2em] uppercase">Avant</div>
        <div className="absolute top-3 right-3 px-2 py-1 rounded bg-violet-500/40 backdrop-blur-md text-[10px] tracking-[0.2em] uppercase">Après</div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="p-2 rounded-md border border-white/10 hover:bg-white/5" data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4" /></button>
        <button onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))} className="p-2 rounded-md border border-white/10 hover:bg-white/5" data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4" /></button>
        <button onClick={reset} className="p-2 rounded-md border border-white/10 hover:bg-white/5" data-testid="reset-btn"><RotateCcw className="w-4 h-4" /></button>
        <button onClick={fullscreen} className="p-2 rounded-md border border-white/10 hover:bg-white/5" data-testid="fullscreen-btn"><Maximize2 className="w-4 h-4" /></button>
        <div className="flex-1" />
        <span className="font-mono text-xs text-muted-foreground">{Math.round(pos)}%</span>
        <span className="font-mono text-xs text-muted-foreground">×{zoom.toFixed(2)}</span>
      </div>
    </div>
  );
}
